import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { catchError, mergeMap, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { RootState } from '@root/app.state';
import { selectUser } from '@platform/platform.selectors';

import { back } from '@platform/router.actions';
import { ToastService } from '@platform/toast/toast.service';

import { InboxService } from '@apps/fiduciary/cockpit/inbox/inbox.service';
import { FileSaveService } from '@platform/file/file-save.service';
import * as FiduciaryActions from './fiduciary.actions';
import { TasksService } from './tasks/tasks.service';

@Injectable()
export class FiduciaryEffect {
  uploadFiduciaryInboxFiles$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FiduciaryActions.uploadFiduciaryInboxFiles),
      switchMap(({ customerSpaceId: customerSpaceId, files, targetInbox }) =>
        this.inboxService.uploadDocuments(customerSpaceId, files, targetInbox).pipe(
          tap(() => this.toastService.showSuccessToast('FIDUCIARY.cockpit.file-upload.success-message')),
          mergeMap(() => [FiduciaryActions.uploadFiduciaryInboxFilesSucceeded({ targetInbox })]),
          catchError((err: HttpErrorResponse) => {
            this.toastService.showErrorToast('FIDUCIARY.cockpit.file-upload.error-message');
            return [
              FiduciaryActions.uploadFiduciaryInboxFilesFailed({
                targetInbox,
                status: err.status,
                errors: err.error,
              }),
            ];
          }),
        ),
      ),
    ),
  );

  downloadFiduciaryInboxFile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FiduciaryActions.downloadFiduciaryInboxFile),
      switchMap(({ customerSpaceId, inbox, fileName, saveFileName }) =>
        this.inboxService.downloadDocument(customerSpaceId, inbox, fileName).pipe(
          tap((content) => {
            this.fileSaveService.saveFile(content, saveFileName);
            this.toastService.showSuccessToast('FIDUCIARY.cockpit.file-download.success-message');
          }),
          mergeMap((content) => [FiduciaryActions.downloadFiduciaryInboxFileSucceeded({ content })]),
          catchError((err: HttpErrorResponse) => {
            this.toastService.showErrorToast('FIDUCIARY.cockpit.file-download.error-message');
            return [FiduciaryActions.downloadFiduciaryInboxFileFailed({ status: err.status, errors: err.error })];
          }),
        ),
      ),
    ),
  );

  loadAllPortalTasksForUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FiduciaryActions.loadAllFiduciaryTasksForUser),
      switchMap(({ customerSpaceId }) =>
        this.tasksService.getPortalTasks(customerSpaceId, true, true, ['Open', 'InProgress', 'Done', 'Denied'], 'All').pipe(
          mergeMap((result) => [FiduciaryActions.loadAllFiduciaryTasksForUserSucceeded({ tasks: result })]),
          catchError((err: HttpErrorResponse) => [
            FiduciaryActions.loadAllFiduciaryTasksForUserFailed({ status: err.status, errors: err.error }),
          ]),
        ),
      ),
    ),
  );

  createTask$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FiduciaryActions.createTask),
      switchMap(({ customerSpaceId, task, files }) =>
        this.tasksService.createPortalTask(customerSpaceId, task, files).pipe(
          tap(() => this.toastService.showSuccessToast('FIDUCIARY.tasks.create-task.success-message')),
          mergeMap((dto) => [FiduciaryActions.createTaskSucceeded({ task: dto }), back()]),
          catchError((err: HttpErrorResponse) => {
            this.toastService.showErrorToast('FIDUCIARY.tasks.create-task.error-message');
            return [FiduciaryActions.createTaskFailed({ status: err.status, errors: err.error })];
          }),
        ),
      ),
    ),
  );

  updateTask$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FiduciaryActions.updateTask),
      withLatestFrom(this.store$),
      switchMap(([{ customerSpaceId, taskId, task }, rootState]) =>
        this.tasksService.updatePortalTask(customerSpaceId, taskId, task).pipe(
          tap(() => this.toastService.showSuccessToast('FIDUCIARY.tasks.update-task.success-message')),
          mergeMap((dto) => {
            const currentUserId = selectUser(rootState).data.id;
            const actions: Action[] = [FiduciaryActions.updateTaskSucceeded({ task: dto })];
            if (dto.assignedTo.id !== currentUserId && dto.createdBy.id !== currentUserId) {
              actions.push(back());
            }

            return actions;
          }),
          catchError((err: HttpErrorResponse) => {
            this.toastService.showErrorToast('FIDUCIARY.tasks.update-task.error-message');
            return [FiduciaryActions.updateTaskFailed({ status: err.status, errors: err.error })];
          }),
        ),
      ),
    ),
  );

  deleteTask$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FiduciaryActions.deleteTask),
      switchMap(({ customerSpaceId, taskId }) =>
        this.tasksService.deletePortalTask(customerSpaceId, taskId).pipe(
          tap(() => this.toastService.showSuccessToast('FIDUCIARY.tasks.delete-task.success-message')),
          mergeMap(() => [FiduciaryActions.deleteTaskSucceeded(), back()]),
          catchError((err: HttpErrorResponse) => {
            this.toastService.showErrorToast('FIDUCIARY.tasks.delete-task.error-message');
            return [FiduciaryActions.deleteTaskFailed({ status: err.status, errors: err.error })];
          }),
        ),
      ),
    ),
  );

  createTaskComment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FiduciaryActions.createTaskComment),
      switchMap(({ customerSpaceId, taskId, comment }) =>
        this.tasksService.createPortalTaskComment(customerSpaceId, taskId, comment).pipe(
          tap(() => this.toastService.showSuccessToast('comments.create-task-comment.success-message')),
          mergeMap((dto) => [FiduciaryActions.createTaskCommentSucceeded({ comments: dto })]),
          catchError((err: HttpErrorResponse) => {
            this.toastService.showErrorToast('comments.create-task-comment.error-message');
            return [FiduciaryActions.createTaskCommentFailed({ status: err.status, errors: err.error })];
          }),
        ),
      ),
    ),
  );

  deleteTaskComment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FiduciaryActions.deleteTaskComment),
      switchMap(({ customerSpaceId, taskId, commentId }) =>
        this.tasksService.deletePortalTaskComment(customerSpaceId, taskId, commentId).pipe(
          tap(() => this.toastService.showSuccessToast('comments.delete-task-comment.success-message')),
          mergeMap((dto) => [FiduciaryActions.deleteTaskCommentSucceeded({ comments: dto })]),
          catchError((err: HttpErrorResponse) => {
            this.toastService.showErrorToast('comments.delete-task-comment.error-message');
            return [FiduciaryActions.deleteTaskCommentFailed({ status: err.status, errors: err.error })];
          }),
        ),
      ),
    ),
  );

  loadTask$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FiduciaryActions.loadTask),
      switchMap(({ customerSpaceId, taskId }) =>
        this.tasksService.getPortalTask(customerSpaceId, taskId).pipe(
          mergeMap((dto) => [
            FiduciaryActions.loadTaskSucceeded({ task: dto }),
            FiduciaryActions.loadTaskCommentsSucceeded({ comments: dto.comments }),
            FiduciaryActions.loadTaskDocumentsSucceeded({ documents: dto.documents }),
          ]),
          catchError((err: HttpErrorResponse) => [
            FiduciaryActions.loadTaskFailed({
              status: err.status,
              errors: err.error,
            }),
          ]),
        ),
      ),
    ),
  );

  loadInboxUploadHistory$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FiduciaryActions.loadInboxUploadHistory),
      switchMap(({ customerSpaceId, inbox }) =>
        this.inboxService.loadInboxUploadHistory(customerSpaceId, inbox).pipe(
          mergeMap((dto) => [FiduciaryActions.loadInboxUploadHistorySucceeded({ histories: dto })]),
          catchError((err: HttpErrorResponse) => [
            FiduciaryActions.loadInboxUploadHistoryFailed({ status: err.status, errors: err.error }),
          ]),
        ),
      ),
    ),
  );

  uploadDocument$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FiduciaryActions.uploadDocument),
      switchMap(({ customerSpaceId, taskId, files }) =>
        this.tasksService.uploadDocument(customerSpaceId, taskId, files).pipe(
          tap(() => this.toastService.showSuccessToast('DOCUMENTS.file-manager.file-upload.success-message')),
          mergeMap((dto) => [FiduciaryActions.uploadDocumentSucceeded({ documents: dto })]),
          catchError((err: HttpErrorResponse) => {
            this.toastService.showErrorToast('DOCUMENTS.file-manager.file-upload.error-message');
            return [FiduciaryActions.uploadDocumentFailed({ status: err.status, errors: err.error })];
          }),
        ),
      ),
    ),
  );

  deleteDocument$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FiduciaryActions.deleteDocument),
      switchMap(({ customerSpaceId, taskId, documentId, showSuccessToast }) =>
        this.tasksService.deletePortalTaskDocument(customerSpaceId, taskId, documentId).pipe(
          tap(() => {
            if (showSuccessToast) {
              this.toastService.showSuccessToast('DOCUMENTS.file-manager.delete-documents.success-message');
            }
          }),
          mergeMap((dto) => [FiduciaryActions.deleteDocumentSucceeded({ documents: dto })]),
          catchError((err: HttpErrorResponse) => {
            this.toastService.showErrorToast('DOCUMENTS.file-manager.delete-documents.error-message');
            return [FiduciaryActions.deleteDocumentFailed({ status: err.status, errors: err.error })];
          }),
        ),
      ),
    ),
  );

  constructor(
    private actions$: Actions,
    private store$: Store<RootState>,
    private inboxService: InboxService,
    private tasksService: TasksService,
    private toastService: ToastService,
    private fileSaveService: FileSaveService,
  ) {}
}
